import { useState, type HTMLAttributes, type Ref } from 'react'
import { useDraggable } from '@dnd-kit/core'
import {
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Warning as WarningIcon,
  Group as TeamIcon,
  Sms as SmsIcon,
  Note as NoteIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material'
import { Client } from '../../services/api'
import { getDaysInStage, AGING_DAYS, needsTeamAssignment } from './clientStatusChips'

interface ClientCardProps {
  client: Client
  onClick: () => void
  onAssignTeam?: (client: Client) => void
  onSendSms?: (client: Client) => void
  onAddNote?: (client: Client) => void
}

// A client with fewer imported connections than this has too thin a network
// for the outreach stages to work from. Mirrors the table's own threshold.
const LOW_NETWORKING_CONTACTS = 5

// How many reasons fit on a card before the rest collapse into a count. Two
// keeps the card two lines tall, which is what lets a column fit on a laptop.
const MAX_VISIBLE_SIGNALS = 2

type Signal = {
  key: string
  label: string
  tooltip: string
  severity: 'blocked' | 'attention'
  onClick?: () => void
}

/**
 * One client on the board. The card answers "does this person need me, and
 * why" rather than listing everything known about them: a left border coloured
 * by the worst outstanding signal, the number of days they have sat in this
 * stage, and the reasons themselves only when there are any.
 */
export default function ClientCard(props: ClientCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: props.client.id,
  })

  // The card itself never moves: the board draws the moving copy in a
  // DragOverlay, because a card translated in place is clipped by its column's
  // scroll box and painted under the columns after it. What stays behind marks
  // the slot the client is leaving.
  return (
    <ClientCardSurface
      {...props}
      ref={setNodeRef}
      dragHandleProps={{ ...attributes, ...listeners }}
      placeholder={isDragging}
    />
  )
}

/** The card as it follows the pointer, for the board's DragOverlay. */
export function ClientCardDragPreview({ client }: { client: Client }) {
  return <ClientCardSurface client={client} lifted />
}

interface ClientCardSurfaceProps extends Omit<ClientCardProps, 'onClick'> {
  onClick?: () => void
  ref?: Ref<HTMLDivElement>
  dragHandleProps?: HTMLAttributes<HTMLDivElement>
  placeholder?: boolean
  lifted?: boolean
}

function ClientCardSurface({
  client,
  onClick,
  onAssignTeam,
  onSendSms,
  onAddNote,
  ref,
  dragHandleProps,
  placeholder = false,
  lifted = false,
}: ClientCardSurfaceProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const menuOpen = Boolean(menuAnchor)

  const daysInStage = getDaysInStage(client.updatedAt)
  const isAging = daysInStage > AGING_DAYS
  const needsTeam = needsTeamAssignment(client)
  const incompleteTasks = client.incompleteTaskCount ?? 0
  const networkingCount = client.networkingContactCount ?? 0
  const lowNetworking = !!client.hasLinkedinImport && networkingCount < LOW_NETWORKING_CONTACTS

  const signals: Signal[] = []
  if (needsTeam) {
    signals.push({
      key: 'team',
      label: 'Assign team',
      tooltip: 'This client needs a team assignment before work can begin',
      severity: 'blocked',
      onClick: onAssignTeam ? () => onAssignTeam(client) : undefined,
    })
  }
  if (incompleteTasks > 0) {
    signals.push({
      key: 'gates',
      label: `${incompleteTasks} gate task${incompleteTasks === 1 ? '' : 's'}`,
      tooltip: 'Gate tasks must be completed before this client can advance',
      severity: 'attention',
    })
  }
  if (lowNetworking) {
    signals.push({
      key: 'network',
      label: `${networkingCount} contact${networkingCount === 1 ? '' : 's'}`,
      tooltip: `Low network coverage — ${networkingCount} connection${networkingCount === 1 ? '' : 's'} imported`,
      severity: 'attention',
    })
  }
  // Ageing is deliberately not a chip: the line above already says how long
  // they have sat here and turns amber when it matters. It still colours the
  // edge, so a stalled client is visible from the top of the column.
  //
  // Scanning a column is meant to be a glance down its left edge, so the
  // accent carries the worst of the signals rather than one per reason.
  const accent = needsTeam
    ? 'error.main'
    : signals.length > 0 || isAging
      ? 'warning.main'
      : undefined

  const visibleSignals = signals.slice(0, MAX_VISIBLE_SIGNALS)
  const hiddenSignals = signals.slice(MAX_VISIBLE_SIGNALS)

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
  }

  const handleMenuClose = () => setMenuAnchor(null)

  const handleMenuAction = (action: () => void) => {
    handleMenuClose()
    action()
  }

  return (
    // The card is not itself a button: it is draggable and it contains
    // buttons. The client's name is the thing you activate, so a keyboard
    // reaches the record without either nesting or a click target that only a
    // mouse can find.
    <Card
      ref={ref}
      {...dragHandleProps}
      // A duplicate of a card still on the board; assistive tech follows the
      // drag through dnd-kit's announcements instead.
      aria-hidden={lifted || undefined}
      variant="outlined"
      sx={{
        mb: lifted ? 0 : 1,
        opacity: placeholder ? 0.5 : 1,
        cursor: lifted ? 'grabbing' : 'grab',
        borderLeft: 3,
        borderLeftColor: accent ?? 'transparent',
        touchAction: 'none',
        boxShadow: lifted ? 6 : undefined,
      }}
    >
      <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={(e) => {
              e.stopPropagation()
              onClick?.()
            }}
            sx={{
              flexGrow: 1,
              minWidth: 0,
              textAlign: 'left',
              typography: 'subtitle2',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {client.name}
          </Link>
          <IconButton
            size="small"
            aria-label={`Actions for ${client.name}`}
            onClick={handleMenuOpen}
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Time in stage and the reasons to look at this client read as one
            status line, so a card stays two lines tall and a column shows
            people rather than whitespace. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          useFlexGap
          sx={{ flexWrap: 'wrap', mt: 0.25 }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isAging ? 'warning.dark' : 'text.secondary',
              fontWeight: isAging ? 600 : 400,
            }}
          >
            {isAging && <WarningIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'text-bottom' }} />}
            {daysInStage}d in stage
          </Typography>
          {visibleSignals.map((signal) => (
              <Tooltip key={signal.key} title={signal.tooltip}>
                <Chip
                  // Icon plus wording on every chip, so the colour is
                  // reinforcement rather than the only thing carrying meaning.
                  icon={signal.severity === 'blocked' ? <TeamIcon /> : <WarningIcon />}
                  label={signal.label}
                  size="small"
                  color={signal.severity === 'blocked' ? 'error' : 'warning'}
                  variant="outlined"
                  onClick={
                    signal.onClick
                      ? (e: React.MouseEvent) => {
                          e.stopPropagation()
                          signal.onClick?.()
                        }
                      : undefined
                  }
                  sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                />
              </Tooltip>
            ))}
            {hiddenSignals.length > 0 && (
              <Tooltip title={hiddenSignals.map((s) => s.tooltip).join(' • ')}>
                <Chip
                  label={`+${hiddenSignals.length}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                />
              </Tooltip>
          )}
        </Stack>
      </CardContent>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {onAssignTeam && (
          <MenuItem onClick={() => handleMenuAction(() => onAssignTeam(client))}>
            <ListItemIcon>
              <TeamIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign Team</ListItemText>
          </MenuItem>
        )}
        {onSendSms && client.phone && (
          <MenuItem onClick={() => handleMenuAction(() => onSendSms(client))}>
            <ListItemIcon>
              <SmsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send SMS</ListItemText>
          </MenuItem>
        )}
        {onAddNote && (
          <MenuItem onClick={() => handleMenuAction(() => onAddNote(client))}>
            <ListItemIcon>
              <NoteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Note</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => onClick && handleMenuAction(onClick)}>
          <ListItemIcon>
            <OpenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  )
}
